import { MessageDescriptor, defineMessages } from 'react-intl';
import { OnboardingIntent } from 'fym-shared/src/music24/onboardingIntent';

/**
 * The copy of the signup and sign-in screen (design: onboarding g1-g10, copy table in
 * docs/superpowers/design-reference/music24-onboarding/README.md). Product copy, not ASD-STE100.
 * `<grad>` marks the part of a headline that gets the brand gradient.
 */
export const authV2Messages = defineMessages({
  eyebrowSignup: { id: 'app.sml.auth.v2.eyebrow.signup', defaultMessage: 'Start free' },
  headlineDefault: {
    id: 'app.sml.auth.v2.headline.default',
    defaultMessage: 'Get your music heard. <grad>Start with one song.</grad>',
  },
  leadDefault: {
    id: 'app.sml.auth.v2.lead.default',
    defaultMessage:
      'Add a song. MUSIC24 makes the posts and the videos, plans your release and posts it on every channel.',
  },
  headlinePromote: { id: 'app.sml.auth.v2.headline.promote', defaultMessage: 'Be everywhere on release day.' },
  leadPromote: {
    id: 'app.sml.auth.v2.lead.promote',
    defaultMessage:
      'MUSIC24 plans every post from the first teaser to six weeks after, and posts each one on time on every channel.',
  },
  headlineAnalytics: { id: 'app.sml.auth.v2.headline.analytics', defaultMessage: 'Know what brings you listeners.' },
  leadAnalytics: {
    id: 'app.sml.auth.v2.lead.analytics',
    defaultMessage: 'Your streams next to every post you make, in one place. Do more of what works.',
  },
  headlinePublicData: {
    id: 'app.sml.auth.v2.headline.publicData',
    defaultMessage: 'Find where your music can grow.',
  },
  leadPublicData: {
    id: 'app.sml.auth.v2.lead.publicData',
    defaultMessage:
      'See which playlists add songs like yours and where your sound is rising. Then aim your next release there.',
  },
  headlineTeams: { id: 'app.sml.auth.v2.headline.teams', defaultMessage: 'Run every release of your roster.' },
  leadTeams: {
    id: 'app.sml.auth.v2.lead.teams',
    defaultMessage:
      'Kits, plans and numbers for each artist, with a small team. Start with one artist; add the rest when you pick a plan.',
  },
  trialPill: { id: 'app.sml.auth.v2.trial.pill', defaultMessage: '{days} days of {plan}' },
  trialLine: { id: 'app.sml.auth.v2.trial.line', defaultMessage: 'No card. Then Free, or from {price} a month.' },
  emailLabel: { id: 'app.sml.auth.v2.email.label', defaultMessage: 'Email' },
  emailPlaceholder: { id: 'app.sml.auth.v2.email.placeholder', defaultMessage: 'you@example.com' },
  continue: { id: 'app.sml.auth.v2.continue', defaultMessage: 'Continue' },
  or: { id: 'app.sml.auth.v2.or', defaultMessage: 'or' },
  apple: { id: 'app.sml.auth.v2.apple', defaultMessage: 'Continue with Apple' },
  google: { id: 'app.sml.auth.v2.google', defaultMessage: 'Continue with Google' },
  oneField: {
    id: 'app.sml.auth.v2.oneField',
    defaultMessage: 'New here? We make your free account. Back again? We sign you in. Same field.',
  },
  legal: {
    id: 'app.sml.auth.v2.legal',
    defaultMessage: 'By continuing you accept the <terms>Terms</terms> and the <privacy>Privacy policy</privacy>.',
  },
  topHaveAccount: { id: 'app.sml.auth.v2.top.haveAccount', defaultMessage: 'Already have an account?' },
  topSignIn: { id: 'app.sml.auth.v2.top.signIn', defaultMessage: 'Sign in' },
  topNew: { id: 'app.sml.auth.v2.top.new', defaultMessage: 'New to MUSIC24?' },
  topStartFree: { id: 'app.sml.auth.v2.top.startFree', defaultMessage: 'Start free' },
  signinEyebrow: { id: 'app.sml.auth.v2.signin.eyebrow', defaultMessage: 'Sign in' },
  signinHeadline: {
    id: 'app.sml.auth.v2.signin.headline',
    defaultMessage: 'Welcome back. <grad>Your releases are waiting.</grad>',
  },
  signinLead: {
    id: 'app.sml.auth.v2.signin.lead',
    defaultMessage: 'Enter your email and we send you a code. No password to remember.',
  },
  signinCta: { id: 'app.sml.auth.v2.signin.cta', defaultMessage: 'Send me a code' },
  signinNewEmail: {
    id: 'app.sml.auth.v2.signin.newEmail',
    defaultMessage: 'No account with this email? We make a free one, with {days} days of {plan}.',
  },
  codeEyebrow: { id: 'app.sml.auth.v2.code.eyebrow', defaultMessage: 'Check your inbox' },
  codeTitle: { id: 'app.sml.auth.v2.code.title', defaultMessage: 'Enter the 6-digit code.' },
  codeLabel: { id: 'app.sml.auth.v2.code.label', defaultMessage: 'The 6-digit code' },
  codeSentTo: { id: 'app.sml.auth.v2.code.sentTo', defaultMessage: 'We sent it to <b>{email}</b>.' },
  codeNoEmail: { id: 'app.sml.auth.v2.code.noEmail', defaultMessage: 'No email?' },
  codeResend: { id: 'app.sml.auth.v2.code.resend', defaultMessage: 'Send a new code' },
  codeChangeEmail: { id: 'app.sml.auth.v2.code.changeEmail', defaultMessage: 'Change email' },
  codeSpam: {
    id: 'app.sml.auth.v2.code.spam',
    defaultMessage: 'Look in spam or promotions too. The code comes from {sender}.',
  },
  codeWrong: {
    id: 'app.sml.auth.v2.code.wrong',
    defaultMessage: 'That code is not right. Check it, or send a new one.',
  },
  codeRateLimit: {
    id: 'app.sml.auth.v2.code.rateLimit',
    defaultMessage: 'Too many tries. Wait a while, then try again later.',
  },
  // Ruling F5: the API sends the real wait. The copy above stays for an answer with no wait.
  codeRateLimitIn: {
    id: 'app.sml.auth.v2.code.rateLimitIn',
    defaultMessage: 'Too many tries. Try again in {minutes, plural, one {# minute} other {# minutes}}.',
  },
  appleUnavailable: {
    id: 'app.sml.auth.v2.apple.unavailable',
    defaultMessage: 'Apple sign-in is not available right now. Use your email, or try again later.',
  },
  codeResent: { id: 'app.sml.auth.v2.code.resent', defaultMessage: 'New code sent. The old one no longer works.' },
  codeResendIn: { id: 'app.sml.auth.v2.code.resendIn', defaultMessage: 'Send again in {time}' },
  emailDisposable: {
    id: 'app.sml.auth.v2.email.disposable',
    defaultMessage: 'Use an email address you keep. We do not accept throwaway addresses.',
  },
  emailInvalid: { id: 'app.sml.auth.v2.email.invalid', defaultMessage: 'That does not look like an email address.' },
  emailMissing: { id: 'app.sml.auth.v2.email.missing', defaultMessage: 'Type your email address.' },
  // The code step for the address with no new email: the code that went out before (QA brk-6).
  haveCode: { id: 'app.sml.auth.v2.email.haveCode', defaultMessage: 'I already have a code' },
  // The hidden Turnstile check of a code send failed. The page already runs the check again.
  securityCheckFailed: {
    id: 'app.sml.auth.v2.securityCheck.failed',
    defaultMessage: 'Tick “Verify you are human” and press Continue again.',
  },
  securityCheckTick: { id: 'app.sml.auth.v2.securityCheck.tick', defaultMessage: 'Tick the box above to go on.' },
  inviteEyebrow: { id: 'app.sml.auth.v2.invite.eyebrow', defaultMessage: 'You are invited' },
  inviteTitle: { id: 'app.sml.auth.v2.invite.title', defaultMessage: 'Sign in to join <grad>{workspace}.</grad>' },
  inviteTitleNoName: {
    id: 'app.sml.auth.v2.invite.titleNoName',
    defaultMessage: 'Sign in to join <grad>the team.</grad>',
  },
  inviteLead: {
    id: 'app.sml.auth.v2.invite.lead',
    defaultMessage:
      '{inviter} invited you to the team. Sign in with the email the invite went to, and you land in the workspace.',
  },
  inviteLeadNoName: {
    id: 'app.sml.auth.v2.invite.leadNoName',
    defaultMessage: 'Sign in with the email the invite went to, and you land in the workspace.',
  },
  inviteNewAccount: {
    id: 'app.sml.auth.v2.invite.newAccount',
    defaultMessage:
      'No account yet? This makes one, joined to the team. No trial, no card: the plan of the workspace covers you.',
  },
  visualLabel: { id: 'app.sml.auth.v2.visual.label', defaultMessage: 'LaziiBee · Forests · Neon' },
  visualAlt: {
    id: 'app.sml.auth.v2.visual.alt',
    defaultMessage: 'Forests by LaziiBee: four feed posts of its Neon release kit',
  },
  visualPhaseSong: { id: 'app.sml.auth.v2.visual.phase.song', defaultMessage: 'Song' },
  visualPhaseKit: { id: 'app.sml.auth.v2.visual.phase.kit', defaultMessage: 'Kit' },
  visualPhasePlan: { id: 'app.sml.auth.v2.visual.phase.plan', defaultMessage: 'Plan' },
  visualPhaseNumbers: { id: 'app.sml.auth.v2.visual.phase.numbers', defaultMessage: 'Numbers' },
});

/** The headline and lead per website intent (design g2-g5). No intent = g1. */
export const authIntentCopy: Record<
  OnboardingIntent | 'default',
  { headline: MessageDescriptor; lead: MessageDescriptor }
> = {
  default: { headline: authV2Messages.headlineDefault, lead: authV2Messages.leadDefault },
  promote: { headline: authV2Messages.headlinePromote, lead: authV2Messages.leadPromote },
  analytics: { headline: authV2Messages.headlineAnalytics, lead: authV2Messages.leadAnalytics },
  publicData: { headline: authV2Messages.headlinePublicData, lead: authV2Messages.leadPublicData },
  teams: { headline: authV2Messages.headlineTeams, lead: authV2Messages.leadTeams },
};
